import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, AlertTriangle, RefreshCw, Calendar } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import MaskedDatePicker from './MaskedDatePicker';
import { formatDate, firstDayOfMonthStr, todayStr } from '../utils/dateUtils';
import { arService } from '../services/arService';

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const PAYMENT_COLORS = { PAID: '#22C55E', PENDING: '#EAB308', OVERDUE: '#EF4444' };

const ARDashboardComponent = () => {
  const [dateFrom, setDateFrom] = useState(firstDayOfMonthStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [billSummary, setBillSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const billRes = await arService.getBillSummary();
      const bill = billRes?.data || billRes?.summary || billRes;
      if (bill && typeof bill === 'object') setBillSummary(bill);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('ARDashboard loadData error:', err);
      setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const paymentStatusData = billSummary ? [
    { name: 'ชำระแล้ว', value: Number(billSummary.Paid_Count || billSummary.paid_count || 0), color: PAYMENT_COLORS.PAID },
    { name: 'รอชำระ', value: Number(billSummary.Pending_Count || billSummary.pending_count || 0), color: PAYMENT_COLORS.PENDING },
    { name: 'เกินกำหนด', value: Number(billSummary.Overdue_Count || billSummary.overdue_count || 0), color: PAYMENT_COLORS.OVERDUE }
  ].filter(d => d.value > 0) : [];

  const totalOutstanding = billSummary?.Total_Outstanding_Amount || billSummary?.total_outstanding_amount || 0;
  const overdueCount = billSummary?.Overdue_Count || billSummary?.overdue_count || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">กำลังโหลดข้อมูลลูกหนี้...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">ลูกหนี้การค้า</h1>
            {lastUpdated && (
              <p className="text-xs text-gray-400 mt-0.5">อัปเดตล่าสุด: {lastUpdated.toLocaleTimeString('th-TH')}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <label className="text-sm text-gray-500 whitespace-nowrap">ตั้งแต่</label>
              <MaskedDatePicker
                value={dateFrom}
                onChange={(str) => setDateFrom(str)}
                placeholderText="DD/MM/YYYY"
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">ถึง</label>
              <MaskedDatePicker
                value={dateTo}
                onChange={(str) => setDateTo(str)}
                placeholderText="DD/MM/YYYY"
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              รีเฟรช
            </button>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            ช่วงวันที่อ้างอิง: <span className="font-medium text-gray-700">{formatDate(dateFrom)}</span>
            {' '}—{' '}
            <span className="font-medium text-gray-700">{formatDate(dateTo)}</span>
          </p>
          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
            ข้อมูลรวมทั้งหมด (ไม่กรองตามวันที่)
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-red-500" />
            </div>
            <span className="text-sm text-gray-500">ยอดค้างชำระรวม</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatAmount(totalOutstanding)}</p>
          <p className="text-xs text-gray-400 mt-1">บาท</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <span className="text-sm text-gray-500">บิลเกินกำหนด</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{Number(overdueCount).toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">บิล</p>
        </div>
      </div>

      {/* Payment Status Donut */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h2 className="text-base font-semibold text-gray-800 mb-4">สถานะการชำระเงิน</h2>
        {paymentStatusData.length > 0 ? (
          <div className="flex flex-col md:flex-row items-center gap-6">
            <ResponsiveContainer width={220} height={220}>
              <PieChart>
                <Pie
                  data={paymentStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {paymentStatusData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} บิล`, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-4 flex-1">
              {paymentStatusData.map((entry, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                    <span className="text-sm text-gray-600">{entry.name}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{entry.value.toLocaleString()} บิล</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-gray-400">ไม่มีข้อมูล</div>
        )}
      </div>
    </div>
  );
};

export default ARDashboardComponent;
